import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Power, ShieldCheck, Users, Search, Save } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABELS = { admin: "Admin", hr_admin: "HR Admin", manager: "Manager", employee: "Employee" };

export default function PermissionsCard() {
  const { refreshUser } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [users, setUsers] = useState([]);
  const [roleDefaults, setRoleDefaults] = useState({});
  const [modules, setModules] = useState({});
  const [savingCfg, setSavingCfg] = useState(false);
  const [selUser, setSelUser] = useState(null);
  const [grants, setGrants] = useState([]);
  const [revokes, setRevokes] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [savingUser, setSavingUser] = useState(false);

  const load = async () => {
    const [c, u] = await Promise.all([api.get("/permissions/config"), api.get("/permissions/users")]);
    setCfg(c.data); setRoleDefaults(c.data.role_defaults || {}); setModules(c.data.modules || {}); setUsers(u.data);
  };
  useEffect(() => { load(); }, []);

  if (!cfg) return <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm text-sm text-slate-500" data-testid="permissions-card">Loading…</div>;

  const perms = cfg.all_permissions;
  const labels = cfg.permission_labels;
  const roles = cfg.roles;
  const editable = cfg.editable_roles; // ["manager","employee"]

  const hasRolePerm = (role, perm) => (roleDefaults[role] || []).includes(perm);
  const toggleRolePerm = (role, perm) => {
    if (!editable.includes(role)) return;
    setRoleDefaults((rd) => {
      const cur = new Set(rd[role] || []);
      cur.has(perm) ? cur.delete(perm) : cur.add(perm);
      return { ...rd, [role]: [...cur] };
    });
  };
  const toggleModule = (m) => setModules((mm) => ({ ...mm, [m]: !mm[m] }));

  const saveConfig = async () => {
    setSavingCfg(true);
    try {
      await api.put("/permissions/config", { role_defaults: roleDefaults, modules });
      toast.success("Permissions & modules saved");
      await refreshUser();
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSavingCfg(false);
  };

  const pickUser = (u) => { setSelUser(u); setGrants([...(u.perm_grants || [])]); setRevokes([...(u.perm_revokes || [])]); };
  const overrideState = (perm) => grants.includes(perm) ? "grant" : (revokes.includes(perm) ? "revoke" : "default");
  const cycleOverride = (perm) => {
    const s = overrideState(perm);
    if (s === "default") { setGrants([...grants, perm]); }
    else if (s === "grant") { setGrants(grants.filter((p) => p !== perm)); setRevokes([...revokes, perm]); }
    else { setRevokes(revokes.filter((p) => p !== perm)); }
  };
  const saveUser = async () => {
    setSavingUser(true);
    try {
      const { data } = await api.put(`/permissions/users/${selUser.id}`, { perm_grants: grants, perm_revokes: revokes });
      toast.success(`Overrides saved for ${selUser.name}`);
      const patched = { ...selUser, perm_grants: data.perm_grants, perm_revokes: data.perm_revokes, effective_permissions: data.effective_permissions };
      setUsers(users.map((u) => u.id === selUser.id ? patched : u));
      setSelUser(patched); setGrants([...data.perm_grants]); setRevokes([...data.perm_revokes]);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSavingUser(false);
  };

  const privileged = selUser && (selUser.role === "admin" || selUser.role === "hr_admin");
  const filteredUsers = users.filter((u) => `${u.name || ""} ${u.email || ""}`.toLowerCase().includes(userSearch.toLowerCase()));

  return (
    <div className="space-y-4" data-testid="permissions-card">
      {/* Module toggles */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <h3 className="font-semibold text-[#0B1A2F] flex items-center gap-2"><Power size={18} /> Module availability</h3>
        <p className="text-sm text-slate-500 mt-1 mb-4">Turn a module off to hide it from staff while you get it ready. Hidden modules disappear from the sidebar, global search and their pages return "unavailable" for everyone — <strong>except Admins, who can still preview them</strong> (shown with an "Off" tag in the sidebar).</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {cfg.soft_modules.map((m) => (
            <label key={m} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-4 py-3">
              <span className="text-sm font-medium text-[#0B1A2F]">{cfg.module_labels[m] || m}</span>
              <Switch checked={modules[m] !== false} onCheckedChange={() => toggleModule(m)} data-testid={`module-toggle-${m}`} />
            </label>
          ))}
        </div>
      </div>

      {/* Role defaults matrix */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <h3 className="font-semibold text-[#0B1A2F] flex items-center gap-2"><ShieldCheck size={18} /> Role permission defaults</h3>
        <p className="text-sm text-slate-500 mt-1 mb-4">Tick the HR permissions each role receives by default. <strong>Admin and HR Admin have built-in full access</strong> and can't be reduced here. Manager and Employee defaults are editable.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="role-matrix">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-4 font-semibold">Permission</th>
                {roles.map((r) => <th key={r} className="py-2 px-3 text-center font-semibold">{ROLE_LABELS[r] || r}</th>)}
              </tr>
            </thead>
            <tbody>
              {perms.map((p) => (
                <tr key={p} className="border-b border-slate-100">
                  <td className="py-2 pr-4 text-[#0B1A2F]">{labels[p] || p}</td>
                  {roles.map((r) => {
                    const locked = !editable.includes(r);
                    const checked = locked ? true : hasRolePerm(r, p);
                    return (
                      <td key={r} className="py-2 px-3 text-center">
                        <Checkbox checked={checked} disabled={locked} onCheckedChange={() => toggleRolePerm(r, p)} data-testid={`matrix-${r}-${p}`} className="mx-auto" />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end mt-4">
          <Button onClick={saveConfig} disabled={savingCfg} className="bg-[#0B1A2F] hover:bg-[#162B48]" data-testid="permissions-save-btn"><Save size={15} className="mr-1.5" /> {savingCfg ? "Saving…" : "Save roles & modules"}</Button>
        </div>
      </div>

      {/* Per-employee overrides */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <h3 className="font-semibold text-[#0B1A2F] flex items-center gap-2"><Users size={18} /> Per-person overrides</h3>
        <p className="text-sm text-slate-500 mt-1 mb-4">Grant or revoke individual permissions for a specific person, on top of their role defaults. Click a permission to cycle <span className="text-emerald-600 font-medium">Granted</span> → <span className="text-rose-600 font-medium">Revoked</span> → Default.</p>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <div className="relative mb-2">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={userSearch} onChange={(e) => setUserSearch(e.target.value)} placeholder="Search people…" className="pl-9" data-testid="override-user-search" />
            </div>
            <div className="max-h-80 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
              {filteredUsers.map((u) => (
                <button key={u.id} onClick={() => pickUser(u)} data-testid={`override-user-${u.id}`}
                  className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-center justify-between gap-2 ${selUser?.id === u.id ? "bg-[#EFF8FF]" : ""}`}>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-[#0B1A2F] truncate">{u.name}</span>
                    <span className="block text-xs text-slate-400 truncate">{u.email}</span>
                  </span>
                  <span className="flex items-center gap-1 shrink-0">
                    <Badge variant="secondary" className="text-[10px]">{ROLE_LABELS[u.role] || u.role}</Badge>
                    {(u.perm_grants?.length > 0 || u.perm_revokes?.length > 0) && <span className="h-2 w-2 rounded-full bg-[#2EA3FD]" title="Has overrides" />}
                  </span>
                </button>
              ))}
              {filteredUsers.length === 0 && <p className="px-3 py-3 text-sm text-slate-400">No matches.</p>}
            </div>
          </div>
          <div>
            {!selUser ? (
              <div className="h-full grid place-items-center text-sm text-slate-400 rounded-lg border border-dashed border-slate-200 p-8" data-testid="override-empty">Select a person to manage their overrides.</div>
            ) : privileged ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="override-privileged">
                <strong>{selUser.name}</strong> is {ROLE_LABELS[selUser.role]} and has built-in full access. Per-person overrides don't apply to Admin or HR Admin accounts.
              </div>
            ) : (
              <div data-testid="override-editor">
                <p className="text-sm font-medium text-[#0B1A2F] mb-2">{selUser.name} <span className="text-xs text-slate-400">({ROLE_LABELS[selUser.role]})</span></p>
                <div className="space-y-1.5">
                  {perms.map((p) => {
                    const s = overrideState(p);
                    const tone = s === "grant" ? "border-emerald-300 bg-emerald-50 text-emerald-700" : s === "revoke" ? "border-rose-300 bg-rose-50 text-rose-700" : "border-slate-200 text-slate-600";
                    const tag = s === "grant" ? "Granted" : s === "revoke" ? "Revoked" : "Default";
                    return (
                      <button key={p} onClick={() => cycleOverride(p)} data-testid={`override-perm-${p}`}
                        className={`w-full flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${tone}`}>
                        <span>{labels[p] || p}</span>
                        <span className="text-xs font-semibold uppercase tracking-wide">{tag}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="mt-3 flex items-center justify-between gap-2">
                  <span className="text-xs text-slate-400">Effective: {(selUser.effective_permissions || []).length} permission(s)</span>
                  <Button onClick={saveUser} disabled={savingUser} className="bg-[#2EA3FD] hover:bg-[#1B8EE6]" data-testid="override-save-btn">{savingUser ? "Saving…" : "Save overrides"}</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
